import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'
import type { DispatchEmailInput } from './dispatch.server'

/**
 * Feature-scoped email triggers.
 *
 * Each function sends one feature's fixed templates. The browser never picks a
 * template name or an arbitrary recipient (the one exception is the
 * admin-only re-send below, which requires an authenticated admin).
 */

const optionalString = z.string().trim().max(2000).optional().nullable()
const email = z.string().trim().email().max(320)

/** Public lead forms that dispatch notification emails. */
export const LEAD_EMAIL_CONTEXTS = [
  'contact-inquiry',
  'estimator-lead',
  'quick-quote',
  'hero-micro',
  'estimate-match',
] as const

export type LeadEmailContext = (typeof LEAD_EMAIL_CONTEXTS)[number]

/**
 * Records a *transport* failure — the browser could not reach the send
 * endpoint at all, so dispatch.server never got the chance to log it.
 *
 * Deliberately unauthenticated (public forms are unauthenticated) but tightly
 * bounded: fixed context enum, no recipient, no template execution, truncated
 * message. It only writes one row to the admin-visible email log.
 */
export const reportLeadEmailFailure = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) =>
    z
      .object({
        context: z.enum(LEAD_EMAIL_CONTEXTS),
        leadId: z.string().trim().max(64),
        message: z.string().trim().max(500).default(''),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { error } = await supabaseAdmin.from('email_send_log').insert({
      message_id: crypto.randomUUID(),
      template_name: `${data.context}-dispatch`,
      recipient_email: 'unreached',
      status: 'failed',
      error_message: `Transport failure: ${data.message || 'unknown error'}`,
      metadata: {
        lead_id: data.leadId,
        lead_context: data.context,
        transport_failure: true,
        note: 'The lead was saved successfully; only the notification email could not be dispatched.',
      },
    } as never)
    if (error) {
      console.error('Failed to record lead email transport failure', error.message)
    }
    return { ok: true }
  })

export const sendContactInquiryEmails = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) =>
    z
      .object({
        leadId: z.string().max(64),
        name: optionalString,
        email,
        phone: optionalString,
        service: optionalString,
        message: optionalString,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { dispatchEmails } = await import('./dispatch.server')
    const meta = { lead_id: data.leadId, lead_kind: 'contact_inquiry' }
    await dispatchEmails([
      {
        templateName: 'contact-inquiry-customer',
        recipientEmail: data.email,
        idempotencyKey: `contact-customer-${data.leadId}`,
        metadata: { ...meta, recipient_kind: 'customer' },
        templateData: { name: data.name, service: data.service },
      },
      {
        templateName: 'contact-inquiry-team',
        idempotencyKey: `contact-team-${data.leadId}`,
        metadata: { ...meta, recipient_kind: 'team' },
        templateData: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          service: data.service,
          message: data.message,
        },
      },
    ])
    return { ok: true }
  })

export const sendEstimateMatchEmails = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) =>
    z
      .object({
        requestId: z.string().max(64),
        name: optionalString,
        email,
        phone: optionalString,
        zip: optionalString,
        linearFeet: z.number().optional().nullable(),
        protectionTypes: z.array(z.string().max(120)).max(20).optional(),
        notes: optionalString,
        competitorQuoteUrl: optionalString,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { dispatchEmails } = await import('./dispatch.server')
    const meta = { lead_id: data.requestId, lead_kind: 'estimate_match_request' }
    await dispatchEmails([
      {
        templateName: 'estimate-match-customer',
        recipientEmail: data.email,
        idempotencyKey: `estimate-customer-${data.requestId}`,
        metadata: { ...meta, recipient_kind: 'customer' },
        templateData: {
          name: data.name,
          linearFeet: data.linearFeet,
          protectionTypes: data.protectionTypes,
        },
      },
      {
        templateName: 'estimate-match-team',
        idempotencyKey: `estimate-team-${data.requestId}`,
        metadata: { ...meta, recipient_kind: 'team' },
        templateData: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          zip: data.zip,
          linearFeet: data.linearFeet,
          protectionTypes: data.protectionTypes,
          notes: data.notes ?? undefined,
          competitorQuoteUrl: data.competitorQuoteUrl ?? undefined,
          requestId: data.requestId,
        },
      },
    ])
    return { ok: true }
  })

export const sendEstimatorLeadEmails = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) =>
    z
      .object({
        leadId: z.string().max(64),
        keyPrefix: z.enum(['estimator-lead', 'quick-quote', 'hero-micro']).default('estimator-lead'),
        name: optionalString,
        email: email.optional().nullable(),
        phone: optionalString,
        linearFeet: z.number().optional().nullable(),
        gates: z.number().optional().nullable(),
        estimateLow: z.number().optional().nullable(),
        estimateHigh: z.number().optional().nullable(),
        pricingMode: optionalString,
        installationOnly: z.boolean().optional(),
        source: optionalString,
        zip: optionalString,
        projectIntent: optionalString,
        propertyType: optionalString,
        terrain: optionalString,
        measurementMethod: optionalString,
        measurementConfidence: optionalString,
        preferText: z.boolean().optional(),
        smsConsent: z.boolean().optional(),
        sendCustomerCopy: z.boolean().default(true),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { dispatchEmails } = await import('./dispatch.server')
    const meta = {
      lead_id: data.leadId,
      lead_kind: 'estimator_lead',
      source: data.source ?? undefined,
      zip: data.zip ?? undefined,
      prefer_text: data.preferText,
      sms_consent: data.smsConsent,
    }

    const sends: DispatchEmailInput[] = [
      {
        templateName: 'estimator-lead-team',
        idempotencyKey: `${data.keyPrefix}-team-${data.leadId}`,
        metadata: { ...meta, recipient_kind: 'team' },
        templateData: {
          name: data.name,
          phone: data.phone,
          email: data.email ?? undefined,
          linearFeet: data.linearFeet ?? 0,
          gates: data.gates ?? 0,
          estimateLow: data.estimateLow ?? 0,
          estimateHigh: data.estimateHigh ?? 0,
          pricingMode: data.pricingMode ?? undefined,
          installationOnly: data.installationOnly,
          source: data.source ?? undefined,
           zip: data.zip ?? undefined,
           projectIntent: data.projectIntent ?? undefined,
           propertyType: data.propertyType ?? undefined,
           terrain: data.terrain ?? undefined,
           measurementMethod: data.measurementMethod ?? undefined,
           measurementConfidence: data.measurementConfidence ?? undefined,
        },
      },
    ]

    if (data.sendCustomerCopy && data.email) {
      sends.push({
        templateName: 'estimator-lead-customer',
        recipientEmail: data.email,
        idempotencyKey: `${data.keyPrefix}-customer-${data.leadId}`,
        metadata: { ...meta, recipient_kind: 'customer' },
        templateData: {
          name: data.name,
          linearFeet: data.linearFeet ?? 0,
          gates: data.gates ?? 0,
          estimateLow: data.estimateLow ?? 0,
          estimateHigh: data.estimateHigh ?? 0,
           pricingMode: data.pricingMode ?? undefined,
           installationOnly: data.installationOnly,
           projectIntent: data.projectIntent ?? undefined,
           propertyType: data.propertyType ?? undefined,
           terrain: data.terrain ?? undefined,
           measurementMethod: data.measurementMethod ?? undefined,
           measurementConfidence: data.measurementConfidence ?? undefined,
        },
      })
    }

    await dispatchEmails(sends)
    return { ok: true }
  })

export const sendDraftFollowupEmails = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) =>
    z
      .object({
        draftId: z.string().max(64),
        trigger: z.enum(['exit-intent', 'idle-timeout']),
        name: optionalString,
        email: email.optional().nullable(),
        phone: optionalString,
        zip: optionalString,
        linearFeet: z.number().optional().nullable(),
        source: optionalString,
        pagePath: optionalString,
        resumeUrl: optionalString,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { dispatchEmails } = await import('./dispatch.server')
    const meta = {
      lead_id: data.draftId,
      lead_kind: 'lead_draft',
      trigger: data.trigger,
    }

    const sends: DispatchEmailInput[] = []
    if (data.email) {
      sends.push({
        templateName: 'draft-followup-customer',
        recipientEmail: data.email,
        idempotencyKey: `draft-customer-${data.draftId}`,
        metadata: { ...meta, recipient_kind: 'customer' },
        templateData: {
          name: data.name,
          zip: data.zip,
          linearFeet: data.linearFeet,
          resumeUrl: data.resumeUrl ?? undefined,
        },
      })
    }
    sends.push({
      templateName: 'draft-followup-team',
      recipientEmail: 'Jason@easelocal.com',
      idempotencyKey: `draft-team-${data.draftId}`,
      metadata: { ...meta, recipient_kind: 'team' },
      templateData: {
        name: data.name,
        phone: data.phone,
        email: data.email ?? undefined,
        zip: data.zip,
        linearFeet: data.linearFeet,
        source: data.source ?? undefined,
        pagePath: data.pagePath ?? undefined,
        trigger: data.trigger,
      },
    })

    await dispatchEmails(sends)
    return { ok: true }
  })

export const sendMaintenanceRequestEmails = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        requestId: z.string().max(64),
        name: optionalString,
        email,
        phone: optionalString,
        address: optionalString,
        preferredDates: optionalString,
        description: optionalString,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { dispatchEmails } = await import('./dispatch.server')
    const meta = { maintenance_request_id: data.requestId }
    await dispatchEmails([
      {
        templateName: 'maintenance-request-team',
        idempotencyKey: `maint-team-${data.requestId}`,
        metadata: { ...meta, recipient_kind: 'team' },
        templateData: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          preferredDates: data.preferredDates,
          description: data.description,
          requestId: data.requestId,
        },
      },
      {
        templateName: 'maintenance-request-customer',
        recipientEmail: data.email,
        idempotencyKey: `maint-cust-${data.requestId}`,
        metadata: { ...meta, recipient_kind: 'customer' },
        templateData: {
          name: data.name,
          description: data.description,
          address: data.address,
          preferredDates: data.preferredDates,
          requestId: data.requestId,
        },
      },
    ])
    return { ok: true }
  })

/** Admin-only re-send of an already-registered template (Leads / Email Log). */
export const adminResendEmail = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        templateName: z.string().max(120),
        recipientEmail: z.string().trim().max(320).optional().nullable(),
        idempotencyKey: z.string().max(200).optional(),
        templateData: z.record(z.string(), z.unknown()).optional(),
        metadata: z.record(z.string(), z.unknown()).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin, error } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (error || !isAdmin) throw new Error('Forbidden')

    const { dispatchEmail } = await import('./dispatch.server')
    return dispatchEmail({
      templateName: data.templateName,
      recipientEmail: data.recipientEmail ?? undefined,
      idempotencyKey: data.idempotencyKey ?? `${data.templateName}-resend-${Date.now()}`,
      metadata: { ...(data.metadata ?? {}), resend: true },
      templateData: data.templateData ?? {},
    })
  })
